import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MCBMP from "../components/assets/MatchCoding.png";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import Spinner from "../components/layout/Spinner";
import { isAllowedEmailUser } from "../components/utils/access";
import {
  fetchAllMyMatches,
  fetchSessionById,
  fetchTeamByNameAndSessionId,
  storeTeam,
} from "../components/utils/dbutils";
import { generateMatch } from "../components/utils/DVWFile";
import { initWithPSVBCompressedBuffer } from "../components/utils/PSVBFile";
import { unzipBuffer } from "../components/utils/Utils";
import { myunzip } from "../components/utils/zip";
import { useLanguage } from "../context/Language/LanguageContext";

const EMPTY_LINEUP = ["4", "3", "2", "5", "6", "1"].map((pos) => ({
  position: pos,
  currentPosition: pos,
  role1: pos === "1" ? "S" : "",
  role2: "",
  playerNumber: "",
  playerFirstName: "",
  playerLastName: "",
}));

const MAX_SQUAD = 14; // FIVB match sheet player limit per team

// Normalise any CSS colour (named or hex) to a "#rrggbb" hex string so it can
// be used as the value of a native <input type="color">.
function toHex(colour) {
  if (!colour) return "#000000";
  if (/^#[0-9a-f]{6}$/i.test(colour)) return colour.toLowerCase();
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.fillStyle = colour;
  return /^#[0-9a-f]{6}$/i.test(ctx.fillStyle) ? ctx.fillStyle : "#000000";
}

// Returns black or white, whichever contrasts better against `colour` — same
// logic as DVMatchCoding's/DrawAndCodeCourt's own `contrastFor` (kept as a
// small local copy here rather than importing from those files).
function contrastFor(colour) {
  if (!colour) return "#ffffff";
  const c = colour.trim();
  const m = c.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55
      ? "#000000"
      : "#ffffff";
  }
  const light = new Set([
    "white", "yellow", "lime", "cyan", "aqua", "silver", "ivory", "snow",
    "cornsilk", "linen", "oldlace", "floralwhite", "seashell", "lightyellow",
    "lightcyan", "lightgreen", "palegreen", "mintcream", "honeydew",
    "aliceblue", "lavender", "lightblue", "lightpink", "lightsalmon",
    "gold", "orange", "khaki", "wheat", "bisque", "moccasin", "peachpuff",
    "mistyrose", "greenyellow", "yellowgreen",
  ]);
  return light.has(c.toLowerCase()) ? "#000000" : "#ffffff";
}

// `fetchTeamByNameAndSessionId` returns `players`/`lineup` already parsed
// into arrays when backed by Firestore, but as raw JSON strings when backed
// by the legacy REST API. Handle both shapes without throwing (a thrown
// JSON.parse on an already-parsed array was silently caught upstream and
// reset the whole team to an empty roster/lineup/default colour — which then
// got persisted by the autosave in `handleStart`, permanently wiping the
// stored roster).
function parseMaybe(value) {
  if (value == null || value === "") return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

async function loadTeamFromDb(teamEntry) {
  const dbteam = await fetchTeamByNameAndSessionId(
    teamEntry.name,
    teamEntry.sessionId
  );
  const team = { ...teamEntry };
  if (dbteam) {
    team.isMBFollowingSetter = dbteam.isMBFollowingSetter ?? false;
    team.isOppPassing = dbteam.isOppPassing ?? false;
    team.logoURL = dbteam.logoURL ?? "";
    team.colour = dbteam.colour || null;
    team.players = parseMaybe(dbteam.players);
    team.lineup = parseMaybe(dbteam.lineup);
  }
  if (!team.players || team.players.length === 0) {
    // Fallback: parse players from the session/match file. Covers both a
    // brand-new team with no stored record yet, AND self-heals a team whose
    // stored roster ended up empty (e.g. from the parse bug above).
    const m = await fetchSessionById(team.sessionId);
    let buffer = myunzip(m.stats);
    if (!buffer) {
      buffer = unzipBuffer(m.stats);
    }
    let mm = null;
    if (buffer.includes("3DATAVOLLEY")) {
      mm = generateMatch(buffer);
    } else {
      mm = initWithPSVBCompressedBuffer(m.stats);
    }
    team.players =
      mm.teamA.Name === team.name ? mm.teamA.players : mm.teamB.players;
  }
  if (!team.lineup || team.lineup.length === 0) {
    team.lineup = EMPTY_LINEUP.map((p) => ({ ...p }));
  }
  team.Name = team.name;
  team.currentLineup = team.lineup.map((p) => ({ ...p }));
  return team;
}

function NewMatch() {
  const navigate = useNavigate();
  const { firebaseUser, checkingStatus } = useAuthStatus();
  const { t: tr } = useLanguage();

  const [teams, setTeams] = useState([]);        // [{name, sessionId}]
  const [loading, setLoading] = useState(true);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);

  const [teamAName, setTeamAName] = useState("");
  const [teamBName, setTeamBName] = useState("");
  const [tournament, setTournament] = useState("");
  const [matchDate, setMatchDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );

  const [selectedTeamA, setSelectedTeamA] = useState(null);
  const [selectedTeamB, setSelectedTeamB] = useState(null);

  // Load distinct team names from the user's match history
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const ms = await fetchAllMyMatches(firebaseUser?.email);
      const tmap = {};
      for (const m of ms) {
        if (m.teamA && !tmap[m.teamA])
          tmap[m.teamA] = { name: m.teamA, sessionId: m.id };
        if (m.teamB && !tmap[m.teamB])
          tmap[m.teamB] = { name: m.teamB, sessionId: m.id };
      }
      setTeams(
        Object.values(tmap).sort((a, b) => a.name.localeCompare(b.name))
      );
      setLoading(false);
    };
    if (firebaseUser) init();
  }, [firebaseUser]);

  const handleTeamChange = async (name, side) => {
    const setter = side === "A" ? setTeamAName : setTeamBName;
    const setSelected = side === "A" ? setSelectedTeamA : setSelectedTeamB;
    const setLoadingTeam = side === "A" ? setLoadingA : setLoadingB;

    setter(name);
    setSelected(null);
    if (!name) return;

    const entry = teams.find((t) => t.name === name);
    if (!entry) return;

    setLoadingTeam(true);
    try {
      const team = await loadTeamFromDb(entry);
      if (!team.colour) team.colour = side === "A" ? "lime" : "magenta";
      setSelected(team);
    } catch (_) {
      setSelected({ ...entry, Name: entry.name, lineup: EMPTY_LINEUP.map(p=>({...p})), players: [], colour: side==="A"?"lime":"magenta" });
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleStart = () => {
    if (!selectedTeamA || !selectedTeamB) return;
    // Best-effort: persist the chosen colour (and other team fields) so it's
    // reused next time this team is selected. Never block navigation on this,
    // and NEVER persist a team whose roster ended up empty — that would wipe
    // out a previously-saved roster (guards against any future regression of
    // the same kind that previously corrupted stored teams).
    if ((selectedTeamA.players ?? []).length > 0) storeTeam(selectedTeamA).catch(() => {});
    if ((selectedTeamB.players ?? []).length > 0) storeTeam(selectedTeamB).catch(() => {});
    navigate("/dvmatchcoding", {
      state: {
        teamA: selectedTeamA,
        teamB: selectedTeamB,
        match: {
          teamA: selectedTeamA.name,
          teamB: selectedTeamB.name,
          tournament,
          matchDate,
          scores: "0-0",
        },
        showSetupModal: true,
      },
    });
  };

  if (checkingStatus) return <Spinner />;

  if (!isAllowedEmailUser(firebaseUser)) {
    return (
      <div className="min-h-screen bg-base-100 pt-8">
        <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
            {tr("← Back")}
          </button>
          <h1 className="text-2xl font-bold">{tr("New Match")}</h1>
        </div>

        <div className="alert alert-info mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6 shrink-0"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <div>
            <h3 className="font-bold">{tr("Match Coding — Coming Soon")}</h3>
            <p className="text-sm">
              {tr(
                "Live match coding is on the way. Here's a preview of what's coming."
              )}
            </p>
          </div>
        </div>

        <img
          src={MCBMP}
          alt={tr("Match Coding")}
          className="w-full rounded-lg border border-base-300 shadow-sm"
        />
        </div>
      </div>
    );
  }

  if (loading) return <Spinner />;

  return (
    <div className="min-h-screen bg-base-100 pt-8">
      <div className="max-w-3xl mx-auto px-4">
      <div className="flex items-center gap-3 mb-6">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(-1)}
        >
          {tr("← Back")}
        </button>
        <h1 className="text-2xl font-bold">{tr("New Match")}</h1>
      </div>

      {/* Teams side by side */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        {/* Team A */}
        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold">
              <span className="text-primary font-mono">*</span> {tr("Team A (Home)")}
            </span>
          </label>
          <div className="flex gap-2 items-center">
            <select
              className="select select-bordered flex-1"
              value={teamAName}
              onChange={(e) => handleTeamChange(e.target.value, "A")}
            >
              <option value="">{tr("Select team…")}</option>
              {teams.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
            {loadingA && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {selectedTeamA && (
              <label className="flex items-center gap-1 text-xs" title={tr("Team colour")}>
                <input
                  type="color"
                  className="w-7 h-7 p-0 border-0 bg-transparent cursor-pointer"
                  value={toHex(selectedTeamA.colour)}
                  onChange={(e) =>
                    setSelectedTeamA({ ...selectedTeamA, colour: e.target.value })
                  }
                />
              </label>
            )}
          </div>
          {selectedTeamA && (
            <div className="text-xs text-success mt-1">✓ {tr("loaded")}</div>
          )}
        </div>

        {/* Team B */}
        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold">
              <span className="text-secondary font-mono">a</span> {tr("Team B (Away)")}
            </span>
          </label>
          <div className="flex gap-2 items-center">
            <select
              className="select select-bordered flex-1"
              value={teamBName}
              onChange={(e) => handleTeamChange(e.target.value, "B")}
            >
              <option value="">{tr("Select team…")}</option>
              {teams.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
            {loadingB && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {selectedTeamB && (
              <label className="flex items-center gap-1 text-xs" title={tr("Team colour")}>
                <input
                  type="color"
                  className="w-7 h-7 p-0 border-0 bg-transparent cursor-pointer"
                  value={toHex(selectedTeamB.colour)}
                  onChange={(e) =>
                    setSelectedTeamB({ ...selectedTeamB, colour: e.target.value })
                  }
                />
              </label>
            )}
          </div>
          {selectedTeamB && (
            <div className="text-xs text-success mt-1">✓ {tr("loaded")}</div>
          )}
        </div>
      </div>

      {/* Tournament */}
      <div className="form-control mb-4">
        <label className="label">
          <span className="label-text">{tr("Tournament (optional)")}</span>
        </label>
        <input
          type="text"
          className="input input-bordered"
          value={tournament}
          onChange={(e) => setTournament(e.target.value)}
          placeholder={tr("Tournament / competition name")}
        />
      </div>

      {/* Date */}
      <div className="form-control mb-8">
        <label className="label">
          <span className="label-text">{tr("Match date")}</span>
        </label>
        <input
          type="date"
          className="input input-bordered"
          value={matchDate}
          onChange={(e) => setMatchDate(e.target.value)}
        />
      </div>

      <div className="flex gap-3">
        <button
          className="btn btn-primary"
          disabled={!selectedTeamA || !selectedTeamB || loadingA || loadingB}
          onClick={handleStart}
        >
          {tr("Start Coding →")}
        </button>
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>
          {tr("Cancel")}
        </button>
      </div>
      </div>
    </div>
  );
}

export default NewMatch;

export function SquadPicker({ players, picks, onChange, onClose, teamColour }) {
  return (
    <div className="mt-2 p-3 rounded-lg bg-base-200 border border-base-300">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium -mt-1">
          Selected
          <span className={`ml-2 font-mono text-xs ${
            picks.size > MAX_SQUAD ? "text-error" : "text-base-content/60"
          }`}>
            {picks.size}/{players.length}
          </span>
        </span>
        <div className="flex gap-1">
          {players.length > MAX_SQUAD && (
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => onChange(new Set(players.slice(0, MAX_SQUAD).map((p) => p.shirtNumber)))}
            >
              Top {MAX_SQUAD}
            </button>
          )}
          <button className="btn btn-xs btn-ghost" onClick={() => onChange(new Set())}>✕</button>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {players.map((p) => {
          const isPicked = picks.has(p.shirtNumber);
          const fullName = [p.FirstName, p.LastName].filter(Boolean).join(" ") || p.NickName || "";
          const pickedStyle = isPicked && teamColour
            ? { backgroundColor: teamColour, borderColor: teamColour, color: contrastFor(teamColour) }
            : undefined;
          return (
            <button
              key={p.shirtNumber}
              className={`btn btn-sm justify-start gap-3 ${isPicked ? (teamColour ? "" : "btn-primary") : "btn-outline"}`}
              style={pickedStyle}
              onClick={() =>
                onChange((prev) => {
                  const n = new Set(prev);
                  if (n.has(p.shirtNumber)) { n.delete(p.shirtNumber); }
                  else if (n.size < MAX_SQUAD) { n.add(p.shirtNumber); }
                  return n;
                })
              }
            >
              <span className="font-mono w-6 text-right shrink-0">{p.shirtNumber}</span>
              <span
                className={`font-normal flex-1 min-w-0 truncate text-left ${
                  fullName.length > 16 ? "text-xs" : ""
                }`}
              >
                {fullName || "—"}
              </span>
            </button>
          );
        })}
      </div>
      {picks.size === 0 && (
        <p className="text-xs text-warning mt-2">Select at least 1 player to proceed.</p>
      )}
    </div>
  );
}
